import { NewStaffTaskNoteBody } from 'models/tasks.types';
import { StaffNoteProps } from 'store/staffNotes/staffNotes.types';
import { getMessageType } from 'utils/helpers';

import { apiSlice } from '../api/apiSlice';
import { addStaffNote } from './staffNotesSlice';

export const staffNotesApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => {
    return {
      createStaffNote: build.mutation<
        StaffNoteProps,
        {
          body: { isUrgent: boolean; note: string; staffNoteFile?: File | null };
          patientId: string;
        }
      >({
        invalidatesTags: ['StaffNote'],
        async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
          const { data: staffNote } = await queryFulfilled;

          const patchResult = dispatch(
            staffNotesApiSlice.util.updateQueryData(
              'initStaffNotes',
              { id: patientId, limit: 10, pageNo: 0 },
              (draftNotes) => {
                draftNotes.staffNotes.push(staffNote);
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
        query: ({ body, patientId }) => {
          const formData = new FormData();
          formData.append('note', body.note);
          if (body.staffNoteFile) {
            formData.append('staffNoteFile', body.staffNoteFile);
          }

          return {
            body: formData,
            method: 'POST',
            url: `/patients/${patientId}/staff-notes`
          };
        },
        transformResponse: (response: { data: StaffNoteProps }) => response.data
      }),
      createTaskStaffNote: build.mutation<
        StaffNoteProps,
        { body: NewStaffTaskNoteBody; taskId: string }
      >({
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Note: we have slice below to avoid circular dependency

            dispatch(addStaffNote(data));
          } catch (err) {
            console.error(err);
          }
        },
        query: ({ body, taskId }) => {
          const formData = new FormData();
          formData.append('note', body.note);
          formData.append('isUrgent', body.isUrgent?.toString() || 'false');

          if (body?.audience) formData.append('audience', body?.audience.join());

          if (body?.staffNoteFile) {
            formData.append('staffNoteFile', body?.staffNoteFile);
          }

          return {
            body: formData,
            method: 'POST',
            url: `/tasks/${taskId}/staff-notes`
          };
        },
        transformResponse: (response: { data: StaffNoteProps }) => response.data
      }),
      deleteStaffNote: build.mutation({
        invalidatesTags: ['StaffNote'],
        query: ({ messageId, type }) => {
          const messagesType = getMessageType(type);

          const params = {
            method: 'DELETE',
            url: `/messages/${messageId}?type=${messagesType}`
          };

          return params;
        }
      }),
      initStaffNotes: build.query<
        { staffNotes: StaffNoteProps[]; totalCount: number },
        { id: string; limit: number; pageNo: number }
      >({
        providesTags: (result) =>
          result
            ? ['StaffNote', ...result.staffNotes.map(({ _id }) => ({ id: _id, type: 'StaffNote' }))]
            : ['StaffNote'],
        query: ({ id, limit, pageNo }) => ({
          params: {
            limit,
            pageNo,
            sortField: 'createdAt',
            sortOrder: 'DESC'
          },
          url: `/patients/${id}/staff-notes`
        }),
        transformResponse: (response: {
          data: StaffNoteProps[];
          info: { totalCount: number };
        }) => ({
          staffNotes: response.data,
          totalCount: response.info.totalCount
        })
      }),
      updateStaffNote: build.mutation({
        invalidatesTags: ['StaffNote'],
        query: ({ message, messageId, type }) => {
          const messagesType = getMessageType(type);

          const params = {
            body: { message },
            method: 'PATCH',
            url: `/messages/${messageId}?type=${messagesType}`
          };

          return params;
        }
      })
    };
  }
});

export const {
  useCreateStaffNoteMutation,
  useCreateTaskStaffNoteMutation,
  useDeleteStaffNoteMutation,
  useLazyInitStaffNotesQuery,
  useUpdateStaffNoteMutation
} = staffNotesApiSlice;
